import { AppConst } from 'constants/appConst';
import type { CompanyImageSearchItemType } from 'types/api/companyImageSearchItemType';
import { uploadS3 } from 'utils/app';

/**
 * MIME type から S3 アップロード用の拡張子を決定するマッピング。
 * ファイル名由来の拡張子は偽装可能なため、MIME type を優先する（M-3）。
 */
const MIME_TO_EXTENSION: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/bmp': 'bmp',
};

/**
 * MIME type から拡張子を取得します。
 * 未知の MIME type の場合は "bin" にフォールバックします。
 */
export function getExtensionFromMimeType(mimeType: string): string {
  return MIME_TO_EXTENSION[mimeType] ?? 'bin';
}

/** 一度にアップロードできるファイルの最大枚数 */
export const MAX_UPLOAD_FILES = 10;

/** アップロード可能な最大ファイルサイズ（10MB） */
export const MAX_FILE_SIZE = 10 * 1024 * 1024;

/**
 * createObjectURL + Image を Promise 化し、画像の幅・高さを取得します。
 * readAsDataURL（Base64 変換）よりメモリ効率が良く、load/error 後に必ず revokeObjectURL を呼びます（M-5）。
 * @param file 対象ファイル
 * @returns 幅・高さのオブジェクト
 */
export function readImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    image.addEventListener('load', () => {
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
      URL.revokeObjectURL(url);
    });

    image.addEventListener('error', err => {
      URL.revokeObjectURL(url);
      reject(err);
    });

    image.src = url;
  });
}

/**
 * ファイルリストに対するバリデーションを行います。
 * バリデーションエラーがある場合はエラーメッセージを返します。
 * すべて通過した場合は null を返します。
 *
 * @param files バリデーション対象のファイル配列
 * @param maxFiles 最大ファイル枚数
 * @param maxFileSize 最大ファイルサイズ（バイト）
 * @param allowedTypes 許可する MIME タイプの配列
 * @returns エラーメッセージ文字列、または null（エラーなし）
 */
export function validateFiles(
  files: File[],
  maxFiles: number,
  maxFileSize: number,
  allowedTypes: string[]
): string | null {
  // 上限チェック
  if (files.length > maxFiles) {
    return `一度にアップロードできるのは${maxFiles}枚までです。`;
  }

  // ファイル形式チェック（不正ファイルが1枚でもあれば全体を拒否）
  const hasInvalidFile = files.some(f => !allowedTypes.includes(f.type));
  if (hasInvalidFile) {
    return 'JPEG、PNG、GIFファイルのみアップロード可能です。';
  }

  // ファイルサイズチェック
  const hasOversizedFile = files.some(f => f.size > maxFileSize);
  if (hasOversizedFile) {
    return '1ファイルあたり10MB以下のファイルのみアップロード可能です。';
  }

  return null;
}

/**
 * アップロード完了した1ファイルの CompanyImageSearchItemType を生成します。
 *
 * @param params アップロード結果とメタデータ
 * @returns CompanyImageSearchItemType
 */
export function buildUploadedImageItem(params: {
  id: number;
  imageKey: string;
  file: File;
  width: number;
  height: number;
}): CompanyImageSearchItemType {
  const { id, imageKey, file, width, height } = params;
  return {
    id,
    name: file.name,
    imageKey,
    imageWidth: width,
    imageHeight: height,
    imageSize: file.size,
    createdAt: new Date().getTime(),
  };
}

/**
 * 失敗したファイルのリストからエラーメッセージを生成します。
 *
 * @param failedFiles 失敗したファイルの配列
 * @returns エラーメッセージ文字列
 */
export function buildFailedErrorMessage(failedFiles: File[]): string {
  const failedList = failedFiles.map(f => `・ ${f.name}`).join('\n');
  return `失敗したファイル：\n${failedList}\n${failedFiles.length}枚のアップロードに失敗しました。`;
}

/**
 * 画像リストを指定のソート種別でソートします。
 * 元の配列を変更せず、新しい配列を返します。
 *
 * @param images ソート対象の画像配列
 * @param sortType ソート種別
 * @returns ソート済みの画像配列
 */
export function sortImages(
  images: CompanyImageSearchItemType[],
  sortType: 'newest' | 'file' | 'oldest'
): CompanyImageSearchItemType[] {
  const sorted = [...images];

  if (sortType === 'newest') {
    sorted.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  } else if (sortType === 'oldest') {
    sorted.sort((a, b) => (a.createdAt > b.createdAt ? 1 : -1));
  } else if (sortType === 'file') {
    sorted.sort((a, b) => (a.name > b.name ? 1 : -1));
  }

  return sorted;
}

/**
 * 複数ファイルのバリデーション・並列アップロードを実行し、
 * 成功した画像アイテムと失敗したファイルの配列を返します。
 *
 * @param params アップロードに必要なパラメータ
 * @returns 成功した CompanyImageSearchItemType 配列と失敗ファイル配列
 */
export async function processFilesUpload(params: {
  files: File[];
  companyId: number;
  postCompanyImageRegister: (request: {
    companyId: number;
    name: string;
    imageWidth: number;
    imageHeight: number;
    imageSize: number;
    uploadKey: string;
  }) => Promise<{ id: number; imageKey: string }>;
}): Promise<{
  succeeded: CompanyImageSearchItemType[];
  failedFiles: File[];
}> {
  const { files, companyId, postCompanyImageRegister } = params;

  const results = await Promise.allSettled(
    files.map(async file => {
      const { width, height } = await readImageDimensions(file);
      // MIME type から拡張子を決定する（file.name 由来は偽装可能なため MIME type を優先）
      const extension = getExtensionFromMimeType(file.type);
      const uploadKey = await uploadS3(file, extension);
      const response = await postCompanyImageRegister({
        companyId,
        name: file.name,
        imageWidth: width,
        imageHeight: height,
        imageSize: file.size,
        uploadKey,
      });
      return buildUploadedImageItem({
        id: response.id,
        imageKey: response.imageKey,
        file,
        width,
        height,
      });
    })
  );

  const succeeded = results
    .filter(
      (r): r is PromiseFulfilledResult<CompanyImageSearchItemType> => r.status === 'fulfilled'
    )
    .map(r => r.value);

  const failedFiles = files.filter((_file, idx) => results[idx].status === 'rejected');

  return { succeeded, failedFiles };
}

/** AppConst.imageTypes への参照（テストで上書きしやすいよう再エクスポート） */
export const imageTypes: string[] = AppConst.imageTypes;
